import type { OrgRole } from '../types/portal'

/**
 * The permission matrix from the specification, as data. The preview store and
 * the interface both read it, and the NestJS RolesGuard should be generated
 * from the same table so the three can never disagree.
 */
export type PortalAction =
  | 'members.invite'
  | 'members.remove'
  | 'members.changeRole'
  | 'members.list'
  | 'org.update'
  | 'projects.create'
  | 'projects.update'
  | 'projects.viewAll'
  | 'projects.viewAssigned'
  | 'tasks.create'
  | 'tasks.update'
  | 'tasks.delete'
  | 'deliverables.upload'
  | 'deliverables.download'
  | 'deliverables.delete'
  | 'messages.view'
  | 'messages.send'
  | 'invoices.viewAll'
  | 'invoices.viewOwn'
  | 'invoices.pay'
  | 'billing.manage'
  | 'payments.view'
  | 'audit.view'

const ALL: OrgRole[] = ['owner', 'admin', 'member', 'viewer']
const MEMBER_UP: OrgRole[] = ['owner', 'admin', 'member']
const ADMIN_UP: OrgRole[] = ['owner', 'admin']
const OWNER: OrgRole[] = ['owner']

const MATRIX: Record<PortalAction, OrgRole[]> = {
  'members.invite': ADMIN_UP,
  'members.remove': ADMIN_UP,
  'members.changeRole': OWNER,
  'members.list': ADMIN_UP,
  'org.update': ADMIN_UP,
  'projects.create': ADMIN_UP,
  'projects.update': ADMIN_UP,
  'projects.viewAll': ADMIN_UP,
  'projects.viewAssigned': ALL,
  'tasks.create': MEMBER_UP,
  'tasks.update': MEMBER_UP,
  'tasks.delete': ADMIN_UP,
  'deliverables.upload': MEMBER_UP,
  'deliverables.download': ALL,
  'deliverables.delete': ADMIN_UP,
  'messages.view': MEMBER_UP,
  'messages.send': MEMBER_UP,
  'invoices.viewAll': ['owner', 'admin'],
  'invoices.viewOwn': MEMBER_UP,
  'invoices.pay': OWNER,
  'billing.manage': OWNER,
  'payments.view': OWNER,
  'audit.view': ADMIN_UP,
}

export function can(role: OrgRole | null | undefined, action: PortalAction): boolean {
  return !!role && MATRIX[action].includes(role)
}

export const ROLE_LABELS: Record<OrgRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
}

export const ROLE_DESCRIPTIONS: Record<OrgRole, string> = {
  owner: 'Full control, including billing and user management.',
  admin: 'Manages users, projects and deliverables, and views billing.',
  member: 'Works on assigned projects, uploads files and sends messages.',
  viewer: 'Read only access to assigned projects and deliverables.',
}
